import React, { memo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { MaskedInput } from '@/components/ui/masked-input';
import { Textarea } from '@/components/ui/textarea';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { ClientFormData } from './ClientFormValidation';
import { LoyaltyStatusSelector } from '@/components/ui/loyalty-status-selector';
import { AddressInput } from '@/components/passport/AddressInput';

interface LegalEntityClientFormProps {
  form: UseFormReturn<ClientFormData>;
  autoFocus?: boolean;
}

export const LegalEntityClientForm = memo(({
  form,
  autoFocus = false
}: LegalEntityClientFormProps) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <FormField 
        control={form.control} 
        name="companyName" 
        render={({ field }) => (
          <FormItem>
            <FormLabel>Название компании *</FormLabel>
            <FormControl>
              <Input 
                {...field} 
                placeholder="ООО «Название компании»" 
                autoFocus={autoFocus} 
                value={field.value || ''} 
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )} 
      />

      <FormField 
        control={form.control} 
        name="legalEntityType" 
        render={({ field }) => (
          <FormItem>
            <FormLabel>Тип организации</FormLabel>
            <FormControl>
              <Input 
                {...field} 
                placeholder="ООО, ИП, АО и т.д." 
                value={field.value || ''} 
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )} 
      />

      <FormField 
        control={form.control} 
        name="phone" 
        render={({ field }) => (
          <FormItem>
            <FormLabel>Телефон *</FormLabel>
            <FormControl>
              <MaskedInput 
                mask="+9 (999) 999-99-99" 
                placeholder="+7 (999) 123-45-67" 
                value={field.value || ''}
                onChange={field.onChange}
                name={field.name}
                onBlur={field.onBlur}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )} 
      />

      <FormField 
        control={form.control} 
        name="email" 
        render={({ field }) => (
          <FormItem>
            <FormLabel>Email</FormLabel>
            <FormControl>
              <Input 
                {...field} 
                type="email" 
                placeholder="company@mail.com" 
                value={field.value || ''} 
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )} 
      />

      <FormField 
        control={form.control} 
        name="inn" 
        render={({ field }) => (
          <FormItem>
            <FormLabel>ИНН</FormLabel>
            <FormControl>
              <Input 
                {...field} 
                placeholder="1234567890" 
                maxLength={12} 
                value={field.value || ''} 
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )} 
      />

      <FormField 
        control={form.control} 
        name="kpp" 
        render={({ field }) => (
          <FormItem>
            <FormLabel>КПП</FormLabel>
            <FormControl>
              <Input 
                {...field} 
                placeholder="123456789" 
                maxLength={9} 
                value={field.value || ''} 
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )} 
      />

      <FormField 
        control={form.control} 
        name="address" 
        render={({ field }) => (
          <FormItem className="md:col-span-2">
            <FormLabel>Юридический адрес</FormLabel>
            <FormControl>
                <AddressInput
                  address={field.value || ''}
                  onAddressChange={(val) => field.onChange(val)}
                  showLabel={false}
                />
            </FormControl>
            <FormMessage />
          </FormItem>
        )} 
      />



      {/* Данные руководителя */}
      <div className="md:col-span-2">
        <h3 className="text-lg font-medium mb-4">Данные руководителя</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField 
            control={form.control} 
            name="directorTitle" 
            render={({ field }) => (
              <FormItem>
                <FormLabel>Должность руководителя</FormLabel>
                <FormControl>
                  <Input 
                    {...field} 
                    placeholder="Директор, Ген. директор, ИП и т.д." 
                    value={field.value || ''} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )} 
          />

          <FormField 
            control={form.control} 
            name="directorName" 
            render={({ field }) => (
              <FormItem>
                <FormLabel>ФИО руководителя</FormLabel>
                <FormControl>
                  <Input 
                    {...field} 
                    placeholder="Иванов Иван Иванович" 
                    value={field.value || ''} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )} 
          />

          <FormField 
            control={form.control} 
            name="legalBasis" 
            render={({ field }) => (
              <FormItem className="md:col-span-2">
                <FormLabel>Основание</FormLabel>
                <FormControl>
                  <Input 
                    {...field} 
                    placeholder="Устав, ОГРНИП № 123456789012345 и т.д." 
                    value={field.value || ''} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )} 
          />
        </div>
      </div>

      {/* Банковские реквизиты */}
      <div className="md:col-span-2">
        <h3 className="text-lg font-medium mb-4">Банковские реквизиты</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField 
            control={form.control} 
            name="bankName" 
            render={({ field }) => (
              <FormItem>
                <FormLabel>Название банка</FormLabel>
                <FormControl>
                  <Input 
                    {...field} 
                    placeholder="Название банка" 
                    value={field.value || ''} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )} 
          />

          <FormField 
            control={form.control} 
            name="bankBik" 
            render={({ field }) => (
              <FormItem>
                <FormLabel>БИК банка</FormLabel>
                <FormControl>
                  <Input 
                    {...field} 
                    placeholder="044525000" 
                    maxLength={9} 
                    value={field.value || ''} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )} 
          />

          <FormField 
            control={form.control} 
            name="bankAccount" 
            render={({ field }) => (
              <FormItem>
                <FormLabel>Расчётный счёт</FormLabel>
                <FormControl>
                  <Input 
                    {...field} 
                    placeholder="40702810000000000000" 
                    value={field.value || ''} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )} 
          />

          <FormField 
            control={form.control} 
            name="bankCorrespondentAccount" 
            render={({ field }) => (
              <FormItem>
                <FormLabel>Корреспондентский счёт</FormLabel>
                <FormControl>
                  <Input 
                    {...field} 
                    placeholder="30101810400000000225" 
                    value={field.value || ''} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )} 
          />
        </div>
      </div>

      {/* Loyalty and Comment Section (2 columns) */}
      <div className="md:col-span-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="loyaltyStatus"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Статус лояльности</FormLabel>
                <FormControl>
                  <LoyaltyStatusSelector
                    value={field.value || 'gray'}
                    onValueChange={field.onChange}
                    size="md"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField 
            control={form.control} 
            name="comment" 
            render={({ field }) => (
              <FormItem>
                <FormLabel>Комментарий</FormLabel>
                <FormControl>
                  <Textarea 
                    {...field} 
                    placeholder="Дополнительная информация о компании" 
                    value={field.value || ''} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )} 
          />
        </div>
      </div>
    </div>
  );
});

LegalEntityClientForm.displayName = 'LegalEntityClientForm';
